import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "inputLabel", "output", "rateNote", "toggle"]

  static values = {
    usdInRate: Number,
    mazaInRate: Number,
    defaultInputCurrency: String,
    syncSelector: String
  }

  connect() {
    this.inputCurrency = this.normalizeCurrency(this.defaultInputCurrencyValue || "USD")
    this.syncToggleState()
    this.recalculate()
  }

  selectInputCurrency(event) {
    this.inputCurrency = this.normalizeCurrency(event.currentTarget.dataset.currency)
    this.syncToggleState()
    this.recalculate()
  }

  recalculate() {
    const amount = this.parseAmount(this.inputTarget.value)
    let usdValue = 0
    let mazaValue = 0

    if (this.inputCurrency === "MAZA") {
      const rate = this.mazaInRateValue
      mazaValue = amount
      usdValue = rate > 0 ? amount / rate : 0

      this.inputLabelTarget.textContent = "MAZA IN"
      this.outputTarget.textContent = rate > 0 ? this.formatAmount(usdValue, "USD") : "N/A"
      this.rateNoteTarget.textContent = rate > 0 ? `MAZA input uses the cash-out lane at ${this.formatNumber(rate)} MAZA per $1.` : "MAZA input rate is currently unavailable."
      this.syncExchangeForm({ mazaValue, usdValue, orderType: "MAZA_IN", rate })
      return
    }

    const rate = this.usdInRateValue
    usdValue = amount
    mazaValue = amount * rate

    this.inputLabelTarget.textContent = "USD IN"
    this.outputTarget.textContent = rate > 0 ? this.formatAmount(mazaValue, "MAZA") : "N/A"
    this.rateNoteTarget.textContent = rate > 0 ? `USD input uses the buy lane at ${this.formatNumber(rate)} MAZA per $1.` : "USD input rate is currently unavailable."
    this.syncExchangeForm({ mazaValue, usdValue, orderType: "USD_IN", rate })
  }

  syncToggleState() {
    this.toggleTargets.forEach((button) => {
      const active = this.normalizeCurrency(button.dataset.currency) === this.inputCurrency
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  normalizeCurrency(value) {
    return String(value || "USD").trim().toUpperCase() === "MAZA" ? "MAZA" : "USD"
  }

  parseAmount(value) {
    const amount = Number.parseFloat(value)
    return Number.isFinite(amount) && amount >= 0 ? amount : 0
  }

  syncExchangeForm({ mazaValue, usdValue, orderType, rate }) {
    if (!this.hasSyncSelectorValue || !this.syncSelectorValue) return

    const form = document.querySelector(this.syncSelectorValue)
    if (!form) return

    const quantityInput = form.querySelector('[data-exchange-target="quantityInput"]')
    const totalDisplay = form.querySelector('[data-exchange-target="totalDisplay"]')
    const rateDisplay = form.querySelector('[data-exchange-target="rateDisplay"]')
    const coinInput = form.querySelector('[data-exchange-target="coinInput"]')
    const usdInput = form.querySelector('[data-exchange-target="usdInput"]')
    const selectedRadio = form.querySelector(`input[name="exchange[order_type]"][value="${orderType}"]`)

    if (!quantityInput || !selectedRadio) return

    const normalizedMaza = this.normalizeMazaForForm(mazaValue, quantityInput)

    quantityInput.value = String(normalizedMaza)
    if (coinInput) coinInput.value = String(normalizedMaza)

    const normalizedUsdValue = rate > 0 ? normalizedMaza / rate : usdValue
    if (usdInput) usdInput.value = normalizedUsdValue.toFixed(2)

    if (rateDisplay) rateDisplay.textContent = rate > 0 ? this.formatNumber(rate) : "N/A"
    if (totalDisplay) totalDisplay.textContent = `$${this.formatNumber(normalizedUsdValue)}`

    selectedRadio.click()
  }

  normalizeMazaForForm(mazaValue, quantityInput) {
    let normalized = Number.isFinite(mazaValue) ? mazaValue : 0
    const min = Number.parseFloat(quantityInput.min)
    const max = Number.parseFloat(quantityInput.max)
    const step = Number.parseFloat(quantityInput.step)

    if (Number.isFinite(step) && step > 0) {
      normalized = Math.round(normalized / step) * step
    }

    if (Number.isFinite(min)) normalized = Math.max(min, normalized)
    if (Number.isFinite(max)) normalized = Math.min(max, normalized)

    return Math.round(normalized)
  }

  formatAmount(value, currency) {
    const formatted = this.formatNumber(value)
    return currency === "USD" ? `$${formatted}` : `${formatted} MAZA`
  }

  formatNumber(value) {
    if (!Number.isFinite(value)) return "0.00"

    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }
}
